import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function ReportUserDialog({ user: reportedUser, open, onOpenChange }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const reportReasons = [
    { value: 'inappropriate_behavior', label: t('report.inappropriateBehavior') },
    { value: 'spam', label: t('report.spam') },
    { value: 'harassment', label: t('report.harassment') },
    { value: 'scam', label: t('report.scam') },
    { value: 'fake_profile', label: t('report.fakeProfile') },
    { value: 'other', label: t('report.other') }
  ];

  const handleSubmitReport = async () => {
    if (!reason || !description.trim()) {
      toast.error(t('report.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reports`, {
        reported_user_id: reportedUser.id,
        reason,
        description
      }, { withCredentials: true });

      toast.success(t('report.submitted'));
      onOpenChange(false);
      setReason('');
      setDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            {t('report.reportUser')} {reportedUser?.name}
          </DialogTitle>
          <DialogDescription>
            {t('report.helpSafe')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">{t('report.reason')}</label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger>
                <SelectValue placeholder={t('report.selectReason')} />
              </SelectTrigger>
              <SelectContent>
                {reportReasons.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">{t('report.details')}</label>
            <Textarea
              rows={4}
              placeholder={t('report.detailsPlaceholder')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <p className="text-xs text-gray-600">
              {t('report.disclaimer')}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmitReport}
            disabled={loading}
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            {loading ? t('common.processing') : t('report.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
